// src/components/admin/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserStats from '../UserStats';
import WelcomeBanner from '../WelcomeBanner';
import ProductStats from '../ProductStats';
import Logo from '../../../images/Logo.png';

const AdminDashboard = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalTransactions: 0,
    co2Reduced: 0,
    wastePrevented: 0,
    activeUsers: 0,
    monthlyGrowth: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/admin/login');
      return;
    }
    fetchDashboardStats();
  }, [isAdmin, navigate]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/admin/dashboard/stats', {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard statistics');
      }

      const data = await response.json();
      setStats({
        totalProducts: data.totalProducts || 0,
        totalTransactions: data.totalTransactions || 0,
        co2Reduced: data.co2Reduced || 0,
        wastePrevented: data.wastePrevented || 0,
        activeUsers: data.activeUsers || 0,
        monthlyGrowth: data.monthlyGrowth || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setError('Failed to load dashboard statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  if (!user || !isAdmin()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-teal-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-8">
              <div className="flex items-center">
                <img
                  src={Logo}
                  alt="cropser-brand-logo"
                  className="h-14 w-20 transition-transform duration-300 hover:scale-105"
                />
                <div className="ml-3">
                  <h1 className="text-xl font-bold text-teal-800">Cropser Admin</h1>
                  <p className="text-xs text-teal-600">Sustainable Agriculture Platform</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-4 bg-teal-50 rounded-2xl px-4 py-2 border border-teal-100">
                <div className="w-10 h-10 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white text-sm font-bold">
                    {user.name?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                  <p className="text-xs text-teal-600 capitalize font-medium">
                    {user.role?.replace('_', ' ')}
                  </p>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="group relative bg-white hover:bg-red-50 text-gray-700 hover:text-red-600 px-4 py-3 rounded-xl border border-gray-200 hover:border-red-200 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                <div className="flex items-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="transition-transform duration-300 group-hover:scale-110">
                    <path fill="currentColor" d="M10.09 15.59L11.5 17l5-5l-5-5l-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59zM19 3H5a2 2 0 0 0-2 2v4h2V5h14v14H5v-4H3v4a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
                  </svg>
                  <span className="text-sm font-medium">Logout</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 sm:px-6 lg:px-8">
        {/* Error Message */}
        {/* {error && (
          <div className="px-4 sm:px-0 mb-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-red-800 text-sm">{error}</span>
                <button
                  onClick={fetchDashboardStats}
                  className="ml-auto bg-red-100 hover:bg-red-200 text-red-800 text-xs px-3 py-1 rounded-lg transition-colors duration-200"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        )} */}

        {/* Welcome Banner */}
        <div className="px-4 sm:px-0 mb-8">
          {/* <div className="relative bg-gradient-to-r from-teal-600 via-teal-700 to-teal-800 rounded-2xl shadow-2xl p-8 text-white overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white bg-opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white bg-opacity-10 rounded-full translate-y-12 -translate-x-12"></div>

            <div className="relative z-10">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold mb-3">
                    Welcome back, System Administrator! 👋
                  </h2>
                  <p className="text-teal-100 text-[13px] mb-4 max-w-2xl">
                    Here's what's happening with your sustainable agriculture platform today.
                  </p>
                  <div className="flex items-center space-x-4 text-teal-100">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm">Platform is live</span>
                    </div>
                    <span>•</span>
                    <span className="text-sm">
                      {loading ? (
                        <span className="inline-block h-4 bg-teal-500 rounded animate-pulse w-20"></span>
                      ) : (
                        `${stats.activeUsers} active users`
                      )}
                    </span>
                    <span>•</span>
                    <span className="text-sm">
                      {loading ? (
                        <span className="inline-block h-4 bg-teal-500 rounded animate-pulse w-16"></span>
                      ) : (
                        `${stats.monthlyGrowth}% growth this month`
                      )}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="bg-white bg-opacity-20 rounded-xl px-4 py-3 backdrop-blur-sm">
                    <p className="text-[12px] text-teal-100">Today's Date</p>
                    <p className="text-[14px] font-semibold">{new Date().toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</p>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          <WelcomeBanner />
        </div> 

        {/* User and Product Analytics Grid */}
        <div className="px-4 sm:px-0 mb-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* User Stats Component */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-6 text-white">
                <h3 className="text-lg font-bold flex items-center">
                  <span className="mr-2">👥</span>
                  User Analytics
                </h3>
                <p className="text-blue-100 text-sm">Platform user statistics and growth</p>
              </div>
              <div className="p-6">
                <UserStats />
              </div>
            </div>

            {/* Product Stats Component */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white">
                <h3 className="text-lg font-bold flex items-center">
                  <span className="mr-2">📦</span>
                  Product Analytics
                </h3>
                <p className="text-emerald-100 text-sm">Farm input statistics and performance</p>
              </div>
              <div className="p-6">
                <ProductStats />
              </div>
            </div>
          </div>
        </div>

        {/* Climate Impact Dashboard */}
        <div className="px-4 sm:px-0">
          <div className="bg-gradient-to-r from-teal-600 to-emerald-700 rounded-2xl shadow-2xl p-8 text-white">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-[14px] font-bold mb-2 flex items-center">
                  Climate Impact Dashboard
                </h3>
                <p className="text-teal-100 text-sm">Tracking your environmental contributions</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-xl px-4 py-2 backdrop-blur-sm">
                <span className="text-[12px] font-semibold">Live Tracking</span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="bg-white bg-opacity-20 rounded-2xl p-6 backdrop-blur-sm border border-white border-opacity-30">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-teal-50">CO₂ Emissions Reduced</h4>
                    <p className="text-3xl font-bold mt-2">
                      {loading ? (
                        <div className="h-10 bg-teal-400 rounded-lg animate-pulse w-32"></div>
                      ) : (
                        `${stats.co2Reduced.toFixed(1)} tons`
                      )}
                    </p>
                  </div>
                  <div className="w-16 h-16 bg-white bg-opacity-30 rounded-2xl flex items-center justify-center">
                    <span className="text-2xl">🌿</span>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-teal-100">
                  <div className="flex justify-between">
                    <span>Equivalent to planting</span>
                    <span className="font-semibold">
                      {loading ? '...' : Math.round(stats.co2Reduced * 50)} trees
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cars off the road</span>
                    <span className="font-semibold">
                      {loading ? '...' : Math.round(stats.co2Reduced * 2.3)} vehicles
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-white bg-opacity-20 rounded-2xl p-6 backdrop-blur-sm border border-white border-opacity-30">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-teal-50">Farm Waste Prevented</h4>
                    <p className="text-3xl font-bold mt-2">
                      {loading ? (
                        <div className="h-10 bg-teal-400 rounded-lg animate-pulse w-32"></div>
                      ) : (
                        `${stats.wastePrevented.toFixed(1)} tons`
                      )}
                    </p>
                  </div>
                  <div className="w-16 h-16 bg-white bg-opacity-30 rounded-2xl flex items-center justify-center">
                    <span className="text-2xl">♻️</span>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-teal-100">
                  <div className="flex justify-between">
                    <span>Inputs saved from expiration</span>
                    <span className="font-semibold">
                      {loading ? '...' : 'Calculating...'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Resource utilization</span>
                    <span className="font-semibold">
                      {loading ? '...' : 'Excellent'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;